import { useCallback, useEffect, useRef, useState } from "react";
import { listGameRuleGeneratedDocumentsPaginated } from "../services/gameRuleGeneratedDocumentService";
import type { GameRuleGeneratedDocumentRow } from "../types/gameRuleGeneratedDocument";

const SEARCH_DEBOUNCE_MS = 400;

export type GameRuleGeneratedDocumentStatusFilter = "all" | "review" | "approved" | "draft";

export type UseGameRuleGeneratedDocumentsResult = {
  readonly rows: readonly GameRuleGeneratedDocumentRow[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly page: number;
  readonly pageSize: number;
  readonly totalRows: number;
  readonly searchInput: string;
  readonly statusFilter: GameRuleGeneratedDocumentStatusFilter;
  readonly setSearchInput: (value: string) => void;
  readonly setStatusFilter: (value: GameRuleGeneratedDocumentStatusFilter) => void;
  readonly setPage: (page: number) => void;
  readonly setPageSize: (pageSize: number) => void;
  readonly reload: () => void;
};

export function useGameRuleGeneratedDocuments(): UseGameRuleGeneratedDocumentsResult {
  const [rows, setRows] = useState<GameRuleGeneratedDocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchInput, setSearchInputState] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilterState] =
    useState<GameRuleGeneratedDocumentStatusFilter>("all");
  const [reloadToken, setReloadToken] = useState(0);
  const searchTimerRef = useRef<number | null>(null);

  const setSearchInput = useCallback((value: string) => {
    setSearchInputState(value);
    setPageState(1);
  }, []);

  const setStatusFilter = useCallback((value: GameRuleGeneratedDocumentStatusFilter) => {
    setStatusFilterState(value);
    setPageState(1);
  }, []);

  const setPage = useCallback((next: number) => {
    setPageState(next);
  }, []);

  const setPageSize = useCallback((next: number) => {
    setPageSizeState(next);
    setPageState(1);
  }, []);

  const reload = useCallback(() => {
    setReloadToken((v) => v + 1);
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const { items, total } = await listGameRuleGeneratedDocumentsPaginated({
          page,
          limit: pageSize,
          status: statusFilter,
          search: search || undefined,
        });
        if (!cancelled) {
          setRows(items);
          setTotalRows(total);
        }
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setTotalRows(0);
          setError(
            e instanceof Error ? e.message : "Unable to load generated game rules.",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, search, statusFilter, reloadToken]);

  return {
    rows,
    loading,
    error,
    page,
    pageSize,
    totalRows,
    searchInput,
    statusFilter,
    setSearchInput,
    setStatusFilter,
    setPage,
    setPageSize,
    reload,
  };
}
